/**
 * SLIP-0010 hardened key derivation for the Ed25519 curve.
 *
 * Ed25519 has no public-key (non-hardened) derivation, so SLIP-0010 only defines hardened
 * child keys — every path segment here must be hardened, and the parser enforces it.
 *
 *   master:  I = HMAC-SHA512(key = "ed25519 seed", data = seed)
 *   child:   I = HMAC-SHA512(key = chainCode,      data = 0x00 || key || ser32(index))
 *   with IL = key (32 bytes), IR = chain code (32 bytes).
 *
 * Verified against the official SLIP-0010 ed25519 test vector 1 in the unit tests.
 */

import { hmac } from '@noble/hashes/hmac';
import { sha512 } from '@noble/hashes/sha512';
import { utf8ToBytes, wipe } from './bytes';
import { InvalidInputError } from './errors';

const ED25519_MASTER_KEY = utf8ToBytes('ed25519 seed');
const HARDENED_OFFSET = 0x80000000;

export interface Slip10Node {
  /** 32-byte Ed25519 private scalar seed. */
  key: Uint8Array;
  /** 32-byte chain code. */
  chainCode: Uint8Array;
}

function splitI(i: Uint8Array): Slip10Node {
  return { key: i.slice(0, 32), chainCode: i.slice(32, 64) };
}

/** SLIP-0010 master node for a BIP-39 seed. */
export function masterNodeFromSeed(seed: Uint8Array): Slip10Node {
  if (!(seed instanceof Uint8Array) || seed.length < 16) {
    throw new InvalidInputError('SLIP-0010 seed must be at least 16 bytes');
  }
  return splitI(hmac(sha512, ED25519_MASTER_KEY, seed));
}

/** One hardened SLIP-0010 step. `index` is the *unhardened* index (0 means 0'). */
export function deriveChild(parent: Slip10Node, index: number): Slip10Node {
  if (!Number.isInteger(index) || index < 0 || index >= HARDENED_OFFSET) {
    throw new InvalidInputError(`SLIP-0010 index out of range: ${index}`);
  }
  const hardened = (index + HARDENED_OFFSET) >>> 0;

  const data = new Uint8Array(1 + 32 + 4);
  data[0] = 0x00;
  data.set(parent.key, 1);
  data[33] = (hardened >>> 24) & 0xff;
  data[34] = (hardened >>> 16) & 0xff;
  data[35] = (hardened >>> 8) & 0xff;
  data[36] = hardened & 0xff;

  const child = splitI(hmac(sha512, parent.chainCode, data));
  wipe(data);
  return child;
}

/**
 * Derives a full hardened path, e.g. `m/44'/6767'/0'/0'/0'`.
 * Both `'` and `H`/`h` are accepted as the hardened marker; unhardened segments are rejected.
 */
export function derivePath(path: string, seed: Uint8Array): Slip10Node {
  if (typeof path !== 'string' || !/^m(\/\d+['hH])*$/.test(path.trim())) {
    throw new InvalidInputError(`Invalid SLIP-0010 path (all segments must be hardened): ${path}`);
  }
  const segments = path.trim().split('/');
  let node = masterNodeFromSeed(seed);

  for (let i = 1; i < segments.length; i += 1) {
    const raw = segments[i];
    const index = parseInt(raw.substring(0, raw.length - 1), 10);
    const next = deriveChild(node, index);
    wipe(node.key);
    wipe(node.chainCode);
    node = next;
  }
  return node;
}
